import React from 'react';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Users, BookOpen, Megaphone, Star, Plus, Eye, ChevronRight, CheckCircle, ArrowRight
} from 'lucide-react';

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 shadow-sm">
      <CardContent className="flex flex-col gap-2 px-4 py-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-2xl font-semibold text-slate-900">{value}</p>
      </CardContent>
    </Card>
  );
}

export default function AuthorOverviewTab({ onNavigate }) {
  const { authorProfile, stats, campaigns, books, isLoading, authorId } = useAuthorDashboard();

  const welcomeName = authorProfile?.penName || authorProfile?.name || 'Author';
  const activeCampaigns = campaigns.filter(c => ['open', 'active', 'note_interest'].includes(c.status));

  // A licence-holder is NOT necessarily an author. Buying a plan creates a
  // licence, not an author profile — `createAuthor` is only called from
  // AuthorSignup. So someone who buys from narratoria.com and then signs in
  // with Google has a Firebase user with no `authorId` claim, lands here via
  // redirectByRoles' no-role fallback, and used to be shown an entirely empty
  // ARC dashboard (zero stats, no campaigns, a getting-started checklist for a
  // product they never asked for).
  //
  // The hub itself works for them — its cards resolve from getMyLicence, which
  // is keyed by uid — so show that and hide the ARC surface until there is an
  // actual author identity behind it. A real author whose claim is still
  // refreshing sees the hub first and the rest a moment later, which is a
  // better failure mode than empty zeroes either way.
  const hasAuthorIdentity = !!authorId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {hasAuthorIdentity ? `Welcome back, ${welcomeName}` : 'Welcome back'}
        </h1>
        <p className="text-sm text-slate-500">
          {hasAuthorIdentity
            ? "Here's what's happening with your ARC campaigns."
            : 'Your Narratoria tools are linked below.'}
        </p>
      </div>

      {/* TWO WORLDS (Julie, 2026-07-29). The hub's three cards used to render
          here. They now live at the ROOT — the commercial toolkit's own home —
          and this ARC dashboard carries a single link across rather than
          embedding World 1 inside World 2. */}
      <a
        href="/"
        className="flex items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3 transition-colors hover:bg-teal-50"
      >
        <div>
          <p className="text-sm font-semibold text-teal-900">Your Narratoria tools</p>
          <p className="text-xs text-teal-800">
            Publishing Checklist, Marketing Compass and Story Bible.
          </p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-teal-700" />
      </a>

      {/* ── ARC dashboard — authors only (see hasAuthorIdentity above) ── */}
      {hasAuthorIdentity && (
      <>
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active Campaigns" value={stats.activeCampaigns} icon={Megaphone} accent="bg-teal-100 text-teal-600" />
        <StatCard label="Total Readers" value={stats.totalReaders} icon={Users} accent="bg-blue-100 text-blue-600" />
        <StatCard label="Reviews Received" value={stats.totalReviews} icon={Star} accent="bg-purple-100 text-purple-600" />
        <StatCard label="Avg Rating" value={stats.avgRating} icon={Star} accent="bg-yellow-100 text-yellow-600" />
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Quick Actions</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="gap-1.5 text-sm"
              onClick={() => onNavigate('books')}
            >
              <Plus className="h-3.5 w-3.5" /> Add Book
            </Button>
            <Button
              variant="outline"
              className="gap-1.5 text-sm"
              onClick={() => onNavigate('campaigns')}
            >
              <Megaphone className="h-3.5 w-3.5" /> Create Campaign
            </Button>
            <Button
              variant="outline"
              className="gap-1.5 text-sm"
              onClick={() => onNavigate('readers')}
            >
              <Eye className="h-3.5 w-3.5" /> View Applications
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Active Campaigns */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Active Campaigns</h2>
          <button
            onClick={() => onNavigate('campaigns')}
            className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
          >
            View all <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        {activeCampaigns.length === 0 ? (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="p-6 text-center">
              <Megaphone className="mx-auto mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-500">No active campaigns yet.</p>
              <Button
                variant="outline"
                className="mt-3 gap-1.5 text-sm"
                onClick={() => onNavigate('campaigns')}
              >
                <Plus className="h-3.5 w-3.5" /> Create Your First Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {activeCampaigns.slice(0, 4).map(campaign => {
              const book = books.find(b => b.id === campaign.bookId);
              return (
                <Card key={campaign.id} className="border-slate-200">
                  <CardContent className="flex items-start gap-3 p-4">
                    {book?.coverUrl ? (
                      <img src={book.coverUrl} alt="" className="h-16 w-11 rounded object-cover" />
                    ) : (
                      <div className="flex h-16 w-11 items-center justify-center rounded bg-slate-100">
                        <BookOpen className="h-4 w-4 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="truncate text-sm font-medium text-slate-900">
                        {campaign.name || book?.title || 'Untitled Campaign'}
                      </h3>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700 border-0 text-[10px]">
                          {campaign.status === 'note_interest' ? 'Noting Interest' : campaign.status === 'active' ? 'Active' : 'Open'}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {campaign.spotsRemaining ?? campaign.spotsAvailable ?? 0} / {campaign.spotsAvailable || 0} spots remaining
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        {campaign.pendingCount || 0} pending applications
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Getting Started Checklist (shown if profile incomplete) */}
      {(!authorProfile?.genres?.length || !books.length) && (
        <Card className="border-teal-200 bg-teal-50/50">
          <CardContent className="p-4">
            <h2 className="mb-3 text-sm font-semibold text-teal-800">Getting Started Checklist</h2>
            <div className="space-y-2">
              {[
                { label: 'Create your account', done: true },
                { label: 'Set up your genres', done: !!authorProfile?.genres?.length },
                { label: 'Add your first book', done: books.length > 0 },
                { label: 'Create an ARC campaign', done: campaigns.length > 0 },
                { label: 'Get your first reader application', done: stats.totalReaders > 0 },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle className={`h-4 w-4 ${item.done ? 'text-teal-600' : 'text-slate-300'}`} />
                  <span className={item.done ? 'text-teal-700 line-through' : 'text-slate-700'}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
}
